import type { Bundle } from '@medplum/fhirtypes';
import { useSubscription } from '@medplum/react-hooks';
import { useCallback, useRef, useState } from 'react';

const isDev = import.meta.env.DEV;

export interface FhirSubscriptionCallbacks {
  onBundle?: (bundle: Bundle) => void;
  onReconnect?: () => void;
}

export interface UseFhirSubscriptionReturn {
  connected: boolean;
}

/**
 * Generic Medplum WebSocket subscription (no resource-type filtering).
 * Messaging uses {@link useCommunicationSubscription} which filters to chat identifiers only.
 */
export function useFhirSubscription(
  criteria: string | undefined,
  callbacks: FhirSubscriptionCallbacks,
  debugLabel = '[Fhir][WS]',
): UseFhirSubscriptionReturn {
  const [connected, setConnected] = useState(false);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const handleBundle = useCallback((bundle: Bundle) => {
    if (isDev) {
      const resources = (bundle.entry ?? [])
        .map((e) => e.resource)
        .filter(Boolean)
        .map((r) => `${r!.resourceType}/${r!.id}`)
        .join(', ');
      console.log(`${debugLabel} Received bundle — entries: [${resources}] criteria: ${criteria}`);
    }
    callbacksRef.current.onBundle?.(bundle);
  }, [criteria, debugLabel]);

  const handleWebSocketOpen = useCallback(() => {
    if (isDev) {
      console.log(`${debugLabel} Connected — criteria:`, criteria);
    }
    setConnected(true);
  }, [criteria, debugLabel]);

  const handleWebSocketClose = useCallback(() => {
    if (isDev) {
      console.log(`${debugLabel} Disconnected — criteria:`, criteria);
    }
    setConnected(false);
  }, [criteria, debugLabel]);

  const handleSubscriptionConnect = useCallback(() => {
    if (isDev) {
      console.log(`${debugLabel} Subscription connected/reconnect — criteria:`, criteria);
    }
    callbacksRef.current.onReconnect?.();
  }, [criteria, debugLabel]);

  const handleError = useCallback((err: Error) => {
    console.warn(`${debugLabel} Subscription error:`, err.message, 'criteria:', criteria);
    setConnected(false);
  }, [criteria, debugLabel]);

  useSubscription(criteria, handleBundle, {
    onWebSocketOpen: handleWebSocketOpen,
    onWebSocketClose: handleWebSocketClose,
    onSubscriptionConnect: handleSubscriptionConnect,
    onError: handleError,
  });

  return { connected };
}
